// O PRO DA INTERFACE NOVA (refino de 30/10/2026): o app é grátis — registro,
// metas, cadernos, clubes e o acervo —, e o que se assina são as TRILHAS.
// Desde 01/11/2026 (PAYWALL_DURO=1) o app é pago, com 7 dias grátis sem
// cartão: os textos seguem modeloPago() (modelo.ts).
//   - CardPro: o cartão "Torne-se Pro", curto, nas telas de entrada.
//   - ProVitrine: a página que explica o que a trilha entrega e leva à compra
//     (a mesma tela de pagamento de sempre, na variante "trilhas").
// O preço que aparece é o do plano único das lojas (plans.ts). No app nativo,
// quem manda é a loja: o rótulo vem dela quando ela responde.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, type Curadoria } from '../../services/apiV2';
import { SINGLE_PLAN, SINGLE_PLAN_PRICE_YEAR, SINGLE_PLAN_PRICE_MONTH, formatBRL } from '../plans';
import { isNativeApp } from '../platform';
import { getOfferingPackages } from '../revenuecat';
import { Icon } from '../icons';
import { NvCapa } from './home-nova';
import { modeloPago, diasDoTeste, soAssinantes } from './modelo';
import './nova.css';

export const PRO_NOME = 'Forjamente Pro';
export const PRO_FRASE = 'Assine e libere todas as trilhas de conhecimento.';

/** Os preços na tela: os da loja no app nativo (quando ela responde), os nossos no site. */
export function usePrecosPro() {
  const [loja, setLoja] = useState<{ ano?: string; mes?: string }>({});
  useEffect(() => {
    if (!isNativeApp()) return;
    let vivo = true;
    getOfferingPackages().then((pkgs: any) => {
      if (!vivo || !Array.isArray(pkgs)) return;
      const de = (id: string) => pkgs.find((p: any) => p?.identifier === id)?.product?.priceString;
      setLoja({ ano: de(`${SINGLE_PLAN}_yearly`), mes: de(`${SINGLE_PLAN}_monthly`) });
    }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  const nossoAno = `R$ ${formatBRL(SINGLE_PLAN_PRICE_YEAR)}`;
  const ano = loja.ano || nossoAno;
  // "Menos de R$ 2,50 por mês" só vale se o preço na tela é o nosso.
  const porMes = !loja.ano || loja.ano.replace(/[\s.]/g, '') === nossoAno.replace(/[\s.]/g, '')
    ? `R$ ${formatBRL(SINGLE_PLAN_PRICE_YEAR / 12)}` : null;
  return { ano, mes: loja.mes || `R$ ${formatBRL(SINGLE_PLAN_PRICE_MONTH)}`, porMes };
}

/** O cartão "Torne-se Pro". compacto = uma linha (Home); senão, com as capas. */
export const CardPro: React.FC<{ onAbrir: () => void; compacto?: boolean; onde: string; capas?: (string | null)[] }> = ({ onAbrir, compacto, onde, capas }) => {
  const { ano } = usePrecosPro();
  const cs = (capas || []).filter(Boolean).slice(0, 3) as string[];
  return (
    <button className={`nv-cardpro ${compacto ? 'compacto' : ''}`} onClick={onAbrir} data-card-pro={onde}>
      {!compacto && cs.length > 0 && (
        <span className="nv-cardpro-capas" aria-hidden="true">
          {cs.map((c, i) => <NvCapa key={i} src={c} w={0} h={0} />)}
        </span>
      )}
      <span className="nv-cardpro-texto">
        <span className="nv-mono nv-cardpro-selo"><Icon name="sparkle" size={12} /> Torne-se Pro</span>
        <b className="nv-serif">{PRO_FRASE}</b>
        {!compacto && <small>O Mentor acompanha a sua leitura, página a página. Todas as trilhas por {ano} por ano.</small>}
      </span>
      <Icon name="arrow-right" size={18} />
    </button>
  );
};

const GANHOS: { icone: string; titulo: string; texto: string }[] = [
  { icone: 'globe', titulo: 'Todas as trilhas', texto: 'As que já abriram e as que forem entrando no acervo enquanto a sua assinatura estiver ativa.' },
  { icone: 'sparkle', titulo: 'O Mentor lendo junto', texto: 'Comentários e insights que se abrem quando você chega na página certa. Nada antes da hora.' },
  { icone: 'target', titulo: 'Desafios para aplicar', texto: 'Tarefas curtas para pôr a ideia em prática no mesmo dia, com a sua sequência marcada.' },
  { icone: 'pen', titulo: 'Caderno de aplicação', texto: 'Perguntas para responder no seu caderno. No fim, você tem o que o livro mudou em você.' },
];

/** A vitrine do Pro: o que é a trilha, o que vem, quanto custa. Cobre a tela. */
export const ProVitrine: React.FC<{
  session: any; pro: boolean; trilhas: boolean;
  onFechar: () => void; onAssinar: () => void; onVerTemporada: () => void;
  /** No teste grátis do app pago: quantos dias faltam (a nota sob o botão). */
  diasDeTeste?: number | null;
  /** O teste ainda não começou: conta da 1ª página registrada. */
  testeEmEspera?: boolean;
}> = ({ session, pro, trilhas, onFechar, onAssinar, onVerTemporada, diasDeTeste, testeEmEspera }) => {
  const [cur, setCur] = useState<Curadoria | null>(null);
  // O APP PAGO (01/11/2026): N dias grátis sem cartão, depois o Pro. Sem ele,
  // os textos de 31/10 (o app grátis, o Pro abre as trilhas).
  const pago = modeloPago();
  const nDias = diasDoTeste();
  const precos = usePrecosPro();
  useEffect(() => {
    let vivo = true;
    api.curadoria(session).then((c) => { if (vivo) setCur(c); }).catch(() => {});
    return () => { vivo = false; };
  }, [session]);
  useEffect(() => {
    document.body.classList.add('nv-pro-aberto');
    return () => document.body.classList.remove('nv-pro-aberto');
  }, []);
  const livros = cur?.temporada.livros || [];
  const abertas = livros.filter((l) => l.travessia === 'disponivel');
  const lanc = abertas[0];
  const capas = livros.filter((l) => l.capa).slice(0, 3);
  const leque = lanc ? [capas.find((l) => l !== lanc), lanc, capas.filter((l) => l !== lanc)[1]].filter(Boolean) as typeof capas : capas;

  return createPortal(
    <div className="nv nv-pro" data-tela="pro" role="dialog" aria-label={PRO_NOME}>
      <button className="nv-voltar nv-mono" onClick={onFechar} data-fechar-pro><Icon name="chevron-left" size={16} />Voltar</button>

      <div className="nv-pro-hero nv-entra">
        <div className="nv-leque" aria-hidden="true">
          {leque.map((l) => <NvCapa key={l.slug} src={l.capa} titulo={l.titulo} w={112} h={168} />)}
        </div>
        <span className="nv-mono" style={{ color: 'var(--ember)' }}>{PRO_NOME}</span>
        <h1 className="nv-serif">Leia o livro. <em>Aprenda de verdade.</em></h1>
        <p className="nv-sub" data-pro-modelo={pago ? 'pago' : 'gratis'}>{soAssinantes()
          ? `Tudo aberto: as trilhas, as notas do Mentor, os desafios e o Caderno — o caminho que acompanha a sua leitura e transforma cada livro em prática. ${precos.ano} por ano, com 7 dias de garantia.`
          : pago
          ? `${nDias} dias grátis, sem cartão, com tudo aberto. Depois, o Pro mantém o app e as trilhas — o caminho que acompanha a sua leitura e transforma cada livro em prática.`
          : 'O app é grátis: registro, metas, cadernos e clubes. O Pro abre as trilhas — o caminho que acompanha a sua leitura e transforma cada livro em prática.'}</p>
      </div>

      <section className="nv-secao" data-pro-ganhos>
        <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>O que o Pro libera</span>
        <div className="nv-ganhos">
          {GANHOS.map((g) => (
            <div key={g.titulo} className="nv-ganho">
              <span className="nv-ganho-icone"><Icon name={g.icone as any} size={18} /></span>
              <div><b>{g.titulo}</b><p>{g.texto}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="nv-secao" data-pro-como>
        <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>Como funciona</span>
        <h2 className="nv-serif">Três passos, <em>um livro por vez.</em></h2>
        <ol className="nv-passos">
          <li><b>Escolha a trilha</b><span>Cada ficha diz o que você vai saber fazer ao terminar.</span></li>
          <li><b>Tenha o livro</b><span>Compre na Amazon pelo botão da ficha ou use o exemplar que já está na sua estante.</span></li>
          <li><b>Leia e registre a página</b><span>O caminho se abre conforme você avança: comentários, insights e desafios.</span></li>
        </ol>
      </section>

      {livros.length > 0 && (
        <section className="nv-secao" data-pro-temporada>
          <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>{cur!.temporada.nome} · {cur!.temporada.titulo}</span>
          <h2 className="nv-serif">{abertas.length === 1 ? 'Uma trilha aberta,' : `${abertas.length} trilhas abertas,`} <em>{livros.length - abertas.length} a caminho.</em></h2>
          <div className="nv-pro-livros">
            {livros.map((l) => (
              <div key={l.slug} className="nv-pro-livro" data-pro-livro={l.slug} data-travessia={l.travessia}>
                <NvCapa src={l.capa} titulo={l.titulo} w={40} h={60} />
                <div style={{ minWidth: 0 }}>
                  <b>{l.titulo}</b>
                  <small>{l.habilidade || l.autor}</small>
                </div>
                <span className={`nv-selo nv-mono ${l.travessia === 'disponivel' ? 'disponivel' : ''}`}>{l.travessia === 'disponivel' ? <><i />Aberta</> : 'Em breve'}</span>
              </div>
            ))}
          </div>
          <button className="nv-link" style={{ marginTop: 12 }} onClick={onVerTemporada} data-pro-ver-temporada>Ver as fichas da temporada</button>
        </section>
      )}

      <section className="nv-secao nv-pro-preco" data-pro-preco>
        {/* A OFERTA ÚNICA (Wesley, 31/10/2026): só anual, libera todas as trilhas
            do acervo. Sem promessa de longo prazo (preço congelado, ritmo de
            temporadas): a oferta vai ser ajustada pelo que o Instagram mostrar.
            Ver docs/forjamente-pro-oferta.md. */}
        <span className="nv-mono nv-pro-tarja" data-plano-fundador>Preço de lançamento</span>
        <div className="nv-pro-valor"><b className="nv-serif">{precos.ano}</b><span>por ano</span></div>
        <p>{precos.porMes ? `Menos de ${precos.porMes} por mês. ` : ''}{pago ? 'Um pagamento por ano libera o app e todas as trilhas do acervo.' : 'Um pagamento por ano libera todas as trilhas do acervo.'}</p>
        <div className="nv-pro-congelado" data-preco-congelado>
          <Icon name="check" size={14} /><span><b>Sem fidelidade.</b> Não quis renovar, é só não renovar{pago ? '. Não assinar não apaga as suas notas nem o caderno.' : ': as suas notas e o seu caderno continuam seus, no app grátis.'}</span>
        </div>
      </section>

      <section className="nv-secao nv-pro-duvidas" data-pro-duvidas>
        <span className="nv-mono" style={{ color: 'var(--ink-3)' }}>Dúvidas</span>
        <details><summary>Preciso comprar o livro?</summary><p>Sim. O Forjamente não mostra nem resume o texto do livro: ele acompanha a sua leitura. Compre na Amazon pelo botão da ficha ou use o seu exemplar.</p></details>
        {soAssinantes() ? (
          <details data-duvida-garantia><summary>Como funciona a garantia?</summary><p>Assinou e não gostou? Você tem 7 dias, contados da compra, para pedir o reembolso: no iPhone, pela Apple (reportaproblem.apple.com); no Android, escreva para contato@forjamente.com.br. Veja o passo a passo em forjamente.com.br/suporte.</p></details>
        ) : pago ? (
          <details data-duvida-teste><summary>Como funciona o teste grátis?</summary><p>Os primeiros {nDias} dias são grátis, sem cartão, com o app inteiro aberto. Depois, para continuar, assine o Pro por {precos.ano} por ano. Não assinar não apaga as suas notas nem o caderno: assinando, tudo volta.</p></details>
        ) : (
          <details><summary>O que continua grátis?</summary><p>Tudo o que não é trilha: registrar a leitura, metas e sequência, cadernos e notas, clubes do livro e o acervo inteiro.</p></details>
        )}
        <details data-duvida-preco><summary>O preço pode mudar?</summary><p>Pode, conforme o acervo cresce. O que você pagou vale pelos 12 meses, e qualquer mudança é avisada antes da renovação.</p></details>
        <details><summary>E se a trilha do livro que eu quero ainda não abriu?</summary><p>Vote nela no Explorar: as travessias saem pela ordem dos votos, e quem assina leva todas as que abrirem enquanto a assinatura estiver ativa.</p></details>
      </section>

      <div className="nv-barra-acoes" data-acoes-pro>
        {pro ? (
          <div className="nv-pro-ja" data-pro-ativo>Você é Pro ✓ · todas as trilhas estão abertas</div>
        ) : (
          <>
            <button className="nv-primario" onClick={onAssinar} data-assinar-pro>Assinar o Pro · {precos.ano}/ano</button>
            {pago && diasDeTeste != null
              ? <small className="nv-pro-nota" data-pro-teste-gratis>{testeEmEspera ? `Teste grátis: ${diasDeTeste} dias, contados da sua 1ª página registrada.` : `Teste grátis: ${diasDeTeste === 1 ? 'falta 1 dia' : `faltam ${diasDeTeste} dias`}.`}</small>
              : trilhas && <small className="nv-pro-nota" data-pro-teste>Sua conta já tem as trilhas liberadas (acesso de teste).</small>}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};
